import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  return <AnimatePresence data-id="element-158">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50" data-id="element-159" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-4 md:inset-10 lg:inset-20 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-160">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-[#E8E2DB] bg-[#FAF8F5]" data-id="element-161">
              <h2 className="text-xl md:text-2xl font-serif font-medium text-[#2D2A26]" data-id="element-162">
                {title}
              </h2>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-[#F0EBE6] hover:bg-[#E8E2DB] flex items-center justify-center transition-colors" aria-label="Close modal" data-id="element-163">
                <X className="w-5 h-5 text-[#5D5852]" data-id="element-164" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8" data-id="element-165">
              <div className="max-w-3xl mx-auto prose prose-stone prose-sm md:prose-base" data-id="element-166">
                {children}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function PrivacyPolicyContent() {
  return <div className="text-[#4A4540] space-y-6" data-id="element-167">
      <p className="text-sm text-[#8A8580]" data-id="element-168">Effective Date: January 1, 2026</p>
      <p className="text-sm text-[#8A8580]" data-id="element-169">
        Website: legendaryreads.com
        <br data-id="element-170" />
        Publisher: Legendary Reads ("we," "our," "us")
      </p>

      <section data-id="element-171">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-172">
          1. Information We Collect
        </h3>
        <p data-id="element-173">
          We collect basic information to process orders, including your name,
          email address, billing/shipping address, and payment information. We
          do not store or have direct access to credit card details — all
          transactions are securely processed through our payment partners
          (Shopify Payments, PayPal, etc.).
        </p>
      </section>

      <section data-id="element-174">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-175">
          2. Use of Information
        </h3>
        <p className="mb-2" data-id="element-176">Your information is used solely for:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-177">
          <li data-id="element-178">Processing and delivering orders</li>
          <li data-id="element-179">Sending order confirmations and updates</li>
          <li data-id="element-180">Customer support communication</li>
          <li data-id="element-181">Improving our store and product experience</li>
        </ul>
        <p className="mt-2" data-id="element-182">
          We never sell, rent, or share your information with third parties for
          marketing purposes.
        </p>
      </section>

      <section data-id="element-183">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-184">
          3. Cookies and Analytics
        </h3>
        <p data-id="element-185">
          Our website may use cookies to enhance user experience and analyze
          site performance. By using our site, you consent to the use of cookies
          in accordance with this policy.
        </p>
      </section>

      <section data-id="element-186">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-187">
          4. Data Security
        </h3>
        <p data-id="element-188">
          We implement appropriate technical and organizational measures to
          protect your personal data from unauthorized access, disclosure,
          alteration, or destruction.
        </p>
      </section>

      <section data-id="element-189">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-190">
          5. External Links
        </h3>
        <p data-id="element-191">
          Our site may contain links to external websites (e.g., YouTube,
          Printful, or affiliate partners). We are not responsible for the
          content or privacy practices of those third-party sites.
        </p>
      </section>

      <section data-id="element-192">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-193">
          6. Minors
        </h3>
        <p data-id="element-194">
          We do not knowingly collect or solicit information from children under
          13 years of age.
        </p>
      </section>

      <section data-id="element-195">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-196">
          7. Disclaimer – Fan Publication Notice
        </h3>
        <p data-id="element-197">
          This publication and website are independent fan creations not
          sponsored, endorsed, or affiliated with Universal City Studios LLC or
          Amblin Entertainment. All trademarks, including "Back to the
          Future™," are the property of their respective owners and are used
          under fair use for descriptive and historical reference.
        </p>
      </section>

      <section data-id="element-198">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-199">
          8. Changes to This Policy
        </h3>
        <p data-id="element-200">
          We may update this Privacy Policy periodically. Updates will be posted
          on this page with a revised effective date.
        </p>
      </section>

      <section data-id="element-201">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-202">
          9. Contact
        </h3>
        <p data-id="element-203">
          If you have questions or requests regarding your data, please contact
          us.
        </p>
      </section>
    </div>;
}
export function TermsOfServiceContent() {
  return <div className="text-[#4A4540] space-y-6" data-id="element-204">
      <p className="text-sm text-[#8A8580]" data-id="element-205">Effective Date: January 1, 2026</p>
      <p className="text-sm text-[#8A8580]" data-id="element-206">
        Website: legendaryreads.com
        <br data-id="element-207" />
        Publisher: Legendary Reads ("we," "our," "us")
      </p>

      <section data-id="element-208">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-209">
          1. Overview
        </h3>
        <p data-id="element-210">
          By accessing or purchasing from legendaryreads.com, you agree to these
          Terms of Service. This website offers collectible books, digital
          editions, and related merchandise created as independent fan tributes.
        </p>
      </section>

      <section data-id="element-211">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-212">
          2. Copyright and Intellectual Property
        </h3>
        <p data-id="element-213">
          All original text, research, commentary, and images appearing on this
          website are © 2026 Legendary Reads. Unauthorized reproduction,
          redistribution, or commercial use of any material is strictly
          prohibited. Use of names or references to the Back to the Future films
          is for descriptive and comparative purposes only under fair use
          principles.
        </p>
      </section>

      <section data-id="element-214">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-215">
          3. Purchases, Delivery & Refunds
        </h3>
        <p className="mb-3" data-id="element-216">
          We want you to be completely satisfied with your purchase. Due to the
          nature of our products (digital books, physical books, mugs, prints,
          and tote bags), our policy is focused on replacements for items that
          arrive damaged or defective during shipping.
        </p>

        <div className="bg-[#FAF8F5] p-4 rounded-lg" data-id="element-217">
          <h4 className="font-medium text-[#2D2A26] mb-2" data-id="element-218">
            📖 Digital Products (E-books and others)
          </h4>
          <p className="text-sm" data-id="element-219">
            <strong data-id="element-220">Delivery:</strong> Instant Download.
          </p>
          <p className="text-sm mt-1" data-id="element-221">
            All sales of digital products are final and are not eligible for a
            refund. Due to the instant and irreversible nature of the delivery
            of this type of content, we cannot process cancellations or refunds
            once the purchase has been completed and access has been granted.
          </p>
        </div>
      </section>

      <section data-id="element-222">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-223">
          4. Limitation of Liability
        </h3>
        <p data-id="element-224">
          We are not responsible for any indirect, incidental, or consequential
          damages resulting from the use or inability to use this website or
          products. This publication is a fan-made historical and educational
          tribute, and should not be interpreted as an official studio release.
        </p>
      </section>

      <section data-id="element-225">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-226">
          5. Governing Law
        </h3>
        <p data-id="element-227">
          These Terms are governed by and construed in accordance with the laws
          of the State of Florida, USA, without regard to conflict of law
          principles.
        </p>
      </section>

      <section data-id="element-228">
        <h3 className="text-lg font-serif font-medium text-[#2D2A26] mb-3" data-id="element-229">
          6. Contact
        </h3>
        <p data-id="element-230">For any questions about these Terms or our products, contact us.</p>
      </section>
    </div>;
}